import { SuggestActionIgnoreExpiration } from 'owa-locstrings/lib/strings/suggestactionignoreexpiration.locstring.json';
import { ChangePermissionsText } from 'owa-locstrings/lib/strings/changepermissionstext.locstring.json';
import { SuggestActionRefreshTheLink } from 'owa-locstrings/lib/strings/suggestactionrefreshthelink.locstring.json';
import {
    SuggestActionRetryRefreshingTheLink,
    SuggestActionAttachACopy,
} from './SharingTipContextMenuItem.locstring.json';
import loc from 'owa-localize';
import { closeLinkContextMenu } from './LinkContextMenu';
import { SharingTipOverlay } from './SharingTipOverlay';
import { canDownloadLink } from '../utils/canDownloadLink';
import { getProviderAndAttachLinkAsCopy } from '../utils/getProviderAndAttachLinkAsCopy';
import { onOpenSharingDialogClicked } from '../utils/onOpenSharingDialogClicked';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { InfoBarMessageRank } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { lazyIgnoreSharingTip, LinkActionStatus, SharingLinkInfo } from 'owa-link-data';

import * as React from 'react';
import {
    refreshExpirationDate,
    SharingTipAction,
    SharingTipInfo,
    SharingTipRecipientInfo,
} from 'owa-sharing-data';
import {
    getInfoIconTooltip,
    getDisplayString,
} from '../utils/contextMenu/sharingTipContextMenuItem/sharingTipStrings';
import {
    logAttachAsCopySharingTipDatapoint,
    logChangePermsSharingTipDatapoint,
    logIgnoreSharingTipDatapoint,
    logRefreshExpirationDatapoint,
} from '../utils/contextMenu/sharingTipContextMenuItem/logSharingTipDatapoints';

const menuItemStyles = require('./LinkContextMenuCustomItems.scss');
const sharingTipStyles = require('./SharingTipContextMenuItem.scss');
const classNames = require('classnames/bind').bind(sharingTipStyles);

export interface SharingTipContextMenuItemProps {
    sharingLinkInfo: SharingLinkInfo;
    sharingIssue: SharingTipInfo;
    composeId: string;
    recipientInfos: SharingTipRecipientInfo[];
}

@observer
export class SharingTipContextMenuItem extends React.Component<SharingTipContextMenuItemProps, {}> {
    render() {
        const { sharingIssue, sharingLinkInfo } = this.props;
        const conditionalStyles = {
            error: sharingIssue.messageRank === InfoBarMessageRank.Error,
        };
        const infoTooltip = getInfoIconTooltip(sharingIssue.id, sharingLinkInfo.linkActionStatus);
        return (
            <div
                className={menuItemStyles.menuItemContainer}
                aria-label={sharingIssue.sharingTipString}
                aria-posinset={1}
                aria-setsize={7}
                aria-disabled="false"
                role="menuitem"
                onClick={null}
                tabIndex={0}>
                <SharingTipOverlay sharingLinkInfo={sharingLinkInfo} sharingIssue={sharingIssue} />
                <div className={menuItemStyles.iconContainer}>
                    <Icon
                        iconName={ControlIcons.Info}
                        className={classNames(sharingTipStyles.sharingTipIcon, conditionalStyles)}
                        title={infoTooltip}
                    />
                </div>
                {this.getSharingTipMessageAndActions(sharingLinkInfo, sharingIssue)}
            </div>
        );
    }

    private getSharingTipMessageAndActions = (
        sharingLinkInfo: SharingLinkInfo,
        sharingIssue: SharingTipInfo
    ) => {
        // Note - as we add more actions we will probably want to move to a mapping from sharingTipAction to button.
        const showIgnore = sharingIssue.sharingTipActions.indexOf(SharingTipAction.ignore) !== -1;
        const showRefresh =
            sharingIssue.sharingTipActions.indexOf(SharingTipAction.refreshExpiration) !== -1 &&
            sharingLinkInfo.sharingInfo.canRefresh;
        const refreshString =
            sharingLinkInfo.linkActionStatus === LinkActionStatus.refreshFailed
                ? loc(SuggestActionRetryRefreshingTheLink)
                : loc(SuggestActionRefreshTheLink);

        const hasManageAccess =
            sharingLinkInfo.sharingInfo.canShareExternally ||
            sharingLinkInfo.sharingInfo.canShareInternally;
        const showChangePerms =
            sharingIssue.sharingTipActions.indexOf(SharingTipAction.changePermission) !== -1 &&
            isFeatureEnabled('doc-attachment-contextualSharingTips') &&
            hasManageAccess;

        const showAttachAsCopy =
            sharingIssue.sharingTipActions.indexOf(SharingTipAction.attachAsACopy) !== -1 &&
            !showChangePerms &&
            canDownloadLink(sharingLinkInfo);

        // We over-write the sharing tip string for when the refresh fails.
        const displayString: string = getDisplayString(
            sharingIssue.id,
            sharingIssue.sharingTipString,
            sharingLinkInfo.linkActionStatus,
            sharingLinkInfo.sharingInfo.expirationDate
        );

        return (
            <div className={menuItemStyles.text}>
                <span className={sharingTipStyles.paddingRight}>{displayString}</span>
                {showRefresh && (
                    <>
                        <button
                            className={sharingTipStyles.actionButton}
                            onClick={this.refreshLink}>
                            {refreshString}
                        </button>
                        <span className={sharingTipStyles.divider}> {' | '} </span>
                    </>
                )}
                {showChangePerms && (
                    <>
                        <button
                            className={sharingTipStyles.actionButton}
                            onClick={this.changePermission}>
                            {loc(ChangePermissionsText)}
                        </button>
                        <span className={sharingTipStyles.divider}> {' | '} </span>
                    </>
                )}
                {showAttachAsCopy && (
                    <>
                        <button
                            className={sharingTipStyles.actionButton}
                            onClick={this.attachACopy}>
                            {loc(SuggestActionAttachACopy)}
                        </button>
                        <span className={sharingTipStyles.divider}> {' | '} </span>
                    </>
                )}
                {/*Note - ignore should always be last*/ showIgnore && (
                    <button className={sharingTipStyles.actionButton} onClick={this.ignoreTip}>
                        {loc(SuggestActionIgnoreExpiration)}
                    </button>
                )}
            </div>
        );
    };

    private refreshLink = (event: React.MouseEvent<HTMLButtonElement>) => {
        refreshExpirationDate(this.props.sharingLinkInfo.linkId);
        logRefreshExpirationDatapoint(this.props.sharingIssue.id);
        event.stopPropagation();
    };

    private changePermission = (event: React.MouseEvent<HTMLButtonElement>) => {
        onOpenSharingDialogClicked(
            this.props.sharingLinkInfo,
            this.props.composeId,
            this.props.recipientInfos
        );
        logChangePermsSharingTipDatapoint(this.props.sharingIssue.id);
        event.stopPropagation();
    };

    private attachACopy = (event: React.MouseEvent<HTMLButtonElement>) => {
        getProviderAndAttachLinkAsCopy(this.props.sharingLinkInfo.linkId);
        logAttachAsCopySharingTipDatapoint(this.props.sharingIssue.id);
        closeLinkContextMenu();
        event.stopPropagation();
    };

    private ignoreTip = (event: React.MouseEvent<HTMLButtonElement>) => {
        lazyIgnoreSharingTip
            .import()
            .then(ignoreSharingTip =>
                ignoreSharingTip(this.props.sharingLinkInfo.linkId, this.props.sharingIssue.id)
            );
        logIgnoreSharingTipDatapoint(this.props.sharingIssue.id);
        event.stopPropagation();
    };
}
